import type { OccurrenceInvestigation } from '@axilon-apps/domains-ontology-ui-state'
import { isLiveRunState } from '../../pages/RunPage/run-status'

export type StageState = 'done' | 'active' | 'waiting' | 'hidden'
export interface InvestigationStages {
  /** A linked run is still running or queued. */
  investigating: boolean
  issue: StageState
  debugInfo: StageState
  actions: StageState
  /** Short label for the row while a run is working, otherwise null. */
  progress: string | null
}

/**
 * A run writes the issue first, then debugging information, then tasks. The
 * stage after the newest one present is the one the run is working on. Without
 * a live run, a stage with nothing in it is hidden rather than shown empty.
 */
export function investigationStages(input: {
  runs: readonly OccurrenceInvestigation[] | undefined
  likelyCause: string | undefined
  debugItems: number
  /** The debugging-info request failed, so the stage must stay visible to show its error. */
  debugItemsFailed?: boolean
  tasks: number
}): InvestigationStages {
  const settledDebugInfo: StageState =
    input.debugItems > 0 || input.debugItemsFailed ? 'done' : 'hidden'
  const investigating = (input.runs ?? []).some(
    (run) => run.status !== undefined && isLiveRunState(run.status)
  )
  if (!investigating) {
    return {
      investigating,
      issue: 'done',
      debugInfo: settledDebugInfo,
      actions: 'done',
      progress: null
    }
  }
  if (input.tasks > 0) {
    return {
      investigating,
      issue: 'done',
      debugInfo: settledDebugInfo,
      actions: 'active',
      progress: 'Writing tasks'
    }
  }
  if (!input.likelyCause && input.debugItems === 0) {
    return {
      investigating,
      issue: 'active',
      debugInfo: 'waiting',
      actions: 'waiting',
      progress: 'Finding the cause'
    }
  }
  return {
    investigating,
    issue: 'done',
    debugInfo: 'active',
    actions: 'waiting',
    progress: input.debugItems
      ? `Gathering debug info · ${input.debugItems}`
      : 'Gathering debug info'
  }
}

/** SVG path for a step trend in a 100 × 28 box; each sample holds until the next. */
export function trendPath(values: readonly number[]): string {
  if (values.length === 0) return ''
  const min = Math.min(...values)
  const span = Math.max(...values) - min || 1
  const step = values.length > 1 ? 100 / (values.length - 1) : 100
  const y = (v: number) => +(23 - ((v - min) / span) * 20).toFixed(2)
  return values
    .map((v, i) => (i === 0 ? `M0 ${y(v)}` : `H${+(i * step).toFixed(2)} V${y(v)}`))
    .join(' ')
}
